import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import { Error } from "../../../../../../models/error";
import { Filters, Searches, Sort, Sorts } from "../../../../../../models/shipeezi.table";
import { Group } from "../../../../../../models/group";
import GroupService from "../../../../../../services/group.service";
import { useAppState } from "Context/StateContext";
import { Status } from "Util/enums";
import { ErrorCodeEnum } from "Util/enums/errorCode";

const useActions = () => {
  const { t: translate } = useTranslation();
  const [groups, setGroups] = useState<Group[]>([]);
  const [currentUpdateGroup, setCurrentUpdateGroup] = useState<Group>();
  const [errorUniqueGroupName, setErrorUniqueGroupName] = useState<boolean>(false);
  const [isMutatingGroup, setIsMutatingGroup] = useState<boolean>(false);
  const [hasErrors, setHasErrors] = useState<boolean>();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();
  const [sorting, setSorting] = useState<Sorts>();
  const { currentUser, statusGroupGrid, setStatusGroupGrid } = useAppState();
  const showAllStatusCode = "showAll";

  const { refetch, isFetching } = useQuery(
    ["groups.list", page, pageSize, filters, searches, sorting, statusGroupGrid],
    () => 
      GroupService.getGroups({
        companyId: currentUser?.companyId,
        page,
        pageSize,
        sortBy: sorting
          ? JSON.stringify([...sorting, ["status", "desc"], ["updatedAt", "desc"]])
          : JSON.stringify([["status", "desc"], ["updatedAt", "desc"]]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        status: statusGroupGrid ? statusGroupGrid : ["0", "1"],
        ...filters,
      }),
    {
      onSuccess: (response) => {
        if (response && response.data) setGroups(response.data);
      },
      onError: (error: AxiosError<Error>) => {
        NotificationToast.error(translate("anErrorOccurredTryAgain"));
        setHasErrors(true);
      },
    }
  );

  const { refetch: refetchGroupsTotal } = useQuery(
    ["groups.total", filters, searches],
    () =>
      GroupService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      onSuccess: (response) => {
        if (response && response.data) setTotal(response.data);
      },
    }
  );

  const { isLoading: isCreating, mutate: createGroup } = useMutation(
    ["groups.create"],
    (group: Group) => {
      setIsMutatingGroup(true);
      return GroupService.createGroup(group)
    },
    {
      onSuccess: (response) => {
        setIsMutatingGroup(false);
        setHasErrors(false);
        setErrorUniqueGroupName(false);
        refetch();
        refetchGroupsTotal();
        NotificationToast.success(translate("groupCreatedSuccessfully", { groupName: response?.data?.name }));
      },
      onError: (error: AxiosError<Error>) => {
        setIsMutatingGroup(false);
        if (error && error.response) {
          if (error.response.data?.errorCode === ErrorCodeEnum.UNIQUE_GROUP_NAME) {
            setErrorUniqueGroupName(true);
          } else {
            NotificationToast.error(translate("anErrorOccurredTryAgain"));
          }
        }
        setHasErrors(true);
      },
    }
  );

  const { isLoading: isUpdating, mutate: updateGroup } = useMutation(
    ["groups.update"],
    (groupObj: { currentGroup: Group; prevGroup: Group }) => {
      setIsMutatingGroup(true);
      setCurrentUpdateGroup(groupObj?.prevGroup);
      return GroupService.updateGroup(groupObj?.currentGroup?.id, groupObj?.currentGroup);
    },
    {
      onSuccess: () => {
        setIsMutatingGroup(false);
        setErrorUniqueGroupName(false);
        setHasErrors(false);
        refetch();
        refetchGroupsTotal();
        NotificationToast.success(translate("groupUpdatedSuccessfully", { groupName: currentUpdateGroup?.name }));
      },
      onError: (error: AxiosError<Error>) => {
        setIsMutatingGroup(false);
        if (error && error.response) {
          if (error.response.data?.errorCode === ErrorCodeEnum.UNIQUE_GROUP_NAME) {
            setErrorUniqueGroupName(true);
          } else {
            NotificationToast.error(translate("anErrorOccurredWhileUpdatingGroup", { groupName: currentUpdateGroup?.name }));
          }
        }
        setHasErrors(true);
      },
    }
  );

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (filters: Filters) => {
    // setFilters(filters);
    setStatusGroupGrid(filters.status);
    setCurrentPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  const statusConverter = (status: number): string => {
    switch (status) {
      case 1:
        return Status.ACTIVE;
      case 0:
        return Status.INACTIVE;
      default:
        return showAllStatusCode;
    }
  }

  const setCurrentPage = (newPage: number) => {
    setPage(newPage > 0 ? newPage - 1 : newPage);
  };

  return {
    groups,
    hasErrors,
    total,
    page,
    pageSize,
    isLoading: isFetching,
    setHasErrors,
    setPageSize,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    updateGroup,
    createGroup,
    refetch,
    refetchGroupsTotal,
    statusConverter,
    errorUniqueGroupName,
    setErrorUniqueGroupName,
    isCreating,
    isUpdating,
    showAllStatusCode,
    isMutatingGroup,
  };
};

export default useActions;